import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play, ListChecks, Sparkles, Leaf } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Motion";
import { LogoMark } from "./Logo";
import { getNetwork, signupUrl } from "@/data/networks";
import { setUtmContent } from "@/lib/utm";

const PERSONAS = [
  { k: "parent", t: "A parent getting our family organised", n: "facebook", h: ["Start with the papers", "you already have."], d: "Birth certificates, the mortgage, the car — drop them in and HearthVault files them. The kids' things follow." },
  { k: "caregiver", t: "Caring for an aging parent", n: "facebook", h: ["One folder both of you", "can open."], d: "Keep Mom's policies, accounts and wishes in a vault you share — with reminders before anything lapses." },
  { k: "advisor", t: "An advisor, attorney or planner", n: "linkedin", h: ["Keep the plan you built", "current."], d: "Hand clients a vault that stays up to date, and a portal that shows you what's missing before the review." },
  { k: "executor", t: "A named executor or trustee", n: "linkedin", h: ["Know where everything is", "before the day."], d: "A read-only profile with the will, the accounts and the plan — ready before you ever need it." },
];

const WANTS = [
  { k: "watch", t: "Show me how it works", d: "The 60-second tour", Icon: Play },
  { k: "checklist", t: "Give me a checklist", d: "Printable, kitchen-table ready", Icon: ListChecks },
  { k: "habits", t: "Small weekly habits", d: "Five minutes, one paper", Icon: Leaf },
  { k: "signup", t: "I'm ready — create my vault", d: "Free, about two minutes", Icon: Sparkles },
];

const slide = { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } };

const Option = ({ onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="group w-full text-left rounded-2xl border border-[#E8DDD3] bg-white p-5 transition-colors duration-300 hover:border-[#1F1A17] flex items-center justify-between gap-4">
    {children}
    <ArrowRight className="h-4 w-4 shrink-0 text-[#1F1A17] transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

const Result = ({ persona, want, onReset }) => {
  const net = getNetwork(persona.n);
  const content = `quiz-${persona.k}-${want.k}`;
  const primary = {
    watch: { to: "/youtube#video", label: "Watch the 60-second tour", Icon: Play },
    checklist: { to: "/checklist", label: "Open the checklist", Icon: ListChecks },
    habits: { to: "/instagram", label: "See the weekly rituals", Icon: Leaf },
  }[want.k];
  return (
    <motion.div {...slide} key="result" className="rounded-[2rem] bg-[#181412] text-[#FBF8F4] p-8 sm:p-12" data-testid="start-here-result">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4A373]">For you · via {net.name}</div>
      <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{persona.h[0]} <em className="text-[#D4A373]">{persona.h[1]}</em></h3>
      <p className="mt-5 max-w-xl text-[#C8BFB8] leading-relaxed">{persona.d}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        {primary ? (
          <Link to={primary.to} onClick={() => setUtmContent(content)} data-testid="start-here-primary-link" className="group inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] h-12 px-6 text-sm font-medium transition-colors duration-300">
            <primary.Icon className="h-4 w-4" /> {primary.label} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        ) : null}
        <a href={signupUrl("hearthside", content)} data-testid="start-here-signup-btn"
          className={`group inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-medium transition-colors duration-300 ${primary ? "border border-[#FBF8F4]/25 hover:bg-[#FBF8F4] hover:text-[#181412]" : "bg-[#B4532A] hover:bg-[#9C421E]"}`}>
          <LogoMark className="h-4 w-4" /> Create your vault — free <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#C8BFB8]">
        <Link to={`/${net.key}`} data-testid="start-here-network-link" className="underline underline-offset-4 decoration-[#D4A373] hover:text-[#FBF8F4]">Read the {net.name} page</Link>
        <button type="button" onClick={onReset} data-testid="start-here-reset-btn" className="inline-flex items-center gap-1.5 hover:text-[#FBF8F4]"><ArrowLeft className="h-3.5 w-3.5" /> Start over</button>
      </div>
    </motion.div>
  );
};

export const StartHere = () => {
  const [persona, setPersona] = useState(null);
  const [want, setWant] = useState(null);
  const step = want ? 3 : persona ? 2 : 1;

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="start-here-section">
      <Reveal className="grid lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Not sure where to start?</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1F1A17]">Two taps, and we'll <em>point you the right way.</em></h2>
          <ol className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#685F58]" data-testid="start-here-steps">
            {["Who", "What", "Go"].map((s, i) => (
              <li key={s} className={`rounded-full px-3 py-1.5 border ${step === i + 1 ? "bg-[#1F1A17] text-[#FBF8F4] border-[#1F1A17]" : "border-[#E8DDD3]"}`}>{i + 1} · {s}</li>
            ))}
          </ol>
        </div>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div {...slide} key="s1" className="grid sm:grid-cols-2 gap-3">
              {PERSONAS.map((p) => (
                <Option key={p.k} onClick={() => setPersona(p)} testId={`start-here-persona-${p.k}`}>
                  <span className="font-serif text-xl tracking-tight text-[#1F1A17]">{p.t}</span>
                </Option>
              ))}
            </motion.div>
          )}
          {step === 2 && (
            <motion.div {...slide} key="s2">
              <button type="button" onClick={() => setPersona(null)} data-testid="start-here-back-btn" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#685F58] hover:text-[#1F1A17]"><ArrowLeft className="h-3.5 w-3.5" /> {persona.t}</button>
              <div className="grid sm:grid-cols-2 gap-3">
                {WANTS.map((w) => (
                  <Option key={w.k} onClick={() => setWant(w)} testId={`start-here-want-${w.k}`}>
                    <span className="flex items-center gap-4">
                      <span className="h-11 w-11 rounded-xl grid place-items-center bg-[#FDEEE8] text-[#B4532A] shrink-0"><w.Icon className="h-5 w-5" /></span>
                      <span><span className="block font-serif text-xl tracking-tight text-[#1F1A17]">{w.t}</span><span className="block text-xs text-[#685F58]">{w.d}</span></span>
                    </span>
                  </Option>
                ))}
              </div>
            </motion.div>
          )}
          {step === 3 && <Result persona={persona} want={want} onReset={() => { setPersona(null); setWant(null); }} />}
        </AnimatePresence>
      </Reveal>
    </section>
  );
};
